import React from 'react';
import { PlatformIcon, StatusPill, PriorityPill, Avatar, DueBadge } from './mccUi';
import { humanDate, PROJECT_STAGES } from './mccConstants';
import MccStatusGroup from './MccStatusGroup';

/**
 * Compact list view for content projects.
 *
 * Props:
 *   items: project docs
 *   nameOf: (uid) => name
 *   onOpenProject: (id) => void
 *   selection?: Set<string>  — currently selected project ids (bulk mode)
 *   onToggleSelect?: (id) => void
 *   showSelect?: boolean
 *   groupByStatus?: boolean   — when true, break list into per-status collapsible sections
 *   stages?: string[]         — override the status order (default PROJECT_STAGES)
 */
export default function MccProjectList({
  items,
  nameOf = (x) => x,
  onOpenProject,
  selection,
  onToggleSelect,
  showSelect = false,
  groupByStatus = false,
  stages = PROJECT_STAGES,
}) {
  if (!items || items.length === 0) {
    return (
      <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg py-8 text-center">
        Nothing to show here.
      </div>
    );
  }

  const renderTable = (rows) => (
    <table className="w-full text-sm">
      <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
        <tr>
          {showSelect && <th className="px-2 py-2 w-8"></th>}
          <th className="px-3 py-2 text-left">Project</th>
          <th className="px-3 py-2 text-left">Platforms</th>
          <th className="px-3 py-2 text-left">Assigned</th>
          <th className="px-3 py-2 text-left">Due</th>
          <th className="px-3 py-2 text-left">Priority</th>
          {!groupByStatus && <th className="px-3 py-2 text-left">Status</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((p) => {
          const platforms = [p.primary_platform, ...(p.additional_platforms || [])].filter(Boolean);
          const isSelected = selection?.has?.(p.id);
          return (
            <tr key={p.id} className={`hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}>
              {showSelect && (
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    checked={!!isSelected}
                    onChange={(e) => { e.stopPropagation(); onToggleSelect?.(p.id); }}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    data-testid={`mcc-list-select-${p.id}`}
                  />
                </td>
              )}
              <td className="px-3 py-2 cursor-pointer" onClick={() => onOpenProject(p.id)}>
                <p className="font-semibold text-slate-900 line-clamp-1">{p.title}</p>
                {p.hook ? (
                  <p className="text-[11px] text-slate-500 italic line-clamp-1 mt-0.5">
                    &ldquo;{p.hook}&rdquo;
                  </p>
                ) : p.description ? (
                  <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{p.description}</p>
                ) : null}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  {platforms.slice(0, 4).map((pl) => <PlatformIcon key={pl} platform={pl} size={18} />)}
                  {platforms.length === 0 && <span className="text-[11px] text-slate-400">—</span>}
                </div>
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  {(p.assigned_to || []).slice(0, 3).map((uid) => <Avatar key={uid} name={nameOf(uid)} size={18} />)}
                  {(p.assigned_to || []).length === 0 && <span className="text-[11px] text-slate-400 italic">Unassigned</span>}
                </div>
              </td>
              <td className="px-3 py-2">
                <span className="text-[11px] text-slate-600">{humanDate(p.due_date)}</span>
                <div className="mt-0.5"><DueBadge dueIso={p.due_date} /></div>
              </td>
              <td className="px-3 py-2"><PriorityPill priority={p.priority} /></td>
              {!groupByStatus && <td className="px-3 py-2"><StatusPill status={p.status} size="sm" /></td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (!groupByStatus) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {renderTable(items)}
      </div>
    );
  }

  // Grouped mode — one collapsible MccStatusGroup per status that has rows.
  const groups = stages
    .map((s) => ({ stage: s, rows: items.filter((i) => i.status === s) }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-3">
      {groups.map((g) => {
        const groupIds = g.rows.map((r) => r.id);
        const selectedInGroup = groupIds.filter((id) => selection?.has?.(id));
        const allSelected = groupIds.length > 0 && selectedInGroup.length === groupIds.length;
        const someSelected = selectedInGroup.length > 0 && !allSelected;

        const selectAll = showSelect && onToggleSelect
          ? {
              checked: allSelected,
              indeterminate: someSelected,
              onChange: (checked) => {
                groupIds.forEach((id) => {
                  const isSelected = selection?.has?.(id);
                  if (checked && !isSelected) onToggleSelect(id);
                  if (!checked && isSelected) onToggleSelect(id);
                });
              },
            }
          : undefined;

        return (
          <MccStatusGroup key={g.stage} status={g.stage} count={g.rows.length} selectAll={selectAll}>
            {renderTable(g.rows)}
          </MccStatusGroup>
        );
      })}
    </div>
  );
}
